import React, { useState, useEffect } from 'react';

const IntegrationForm = ({ integration, integrationTypes, selectedIntegrationType, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    type_id: '',
    api_key: '',
    api_endpoint: '',
    enabled: true,
    additional_config: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    if (integration) {
      // Editing existing integration
      setFormData({
        name: integration.name || '',
        type_id: integration.type_id || '',
        api_key: '', // Don't populate API key for security
        api_endpoint: integration.api_endpoint || '',
        enabled: integration.enabled !== undefined ? integration.enabled : true,
        additional_config: integration.additional_config || {}
      });
    } else if (selectedIntegrationType) {
      // Creating new integration with pre-selected type
      setFormData(prev => ({
        ...prev,
        type_id: selectedIntegrationType.type_id,
        name: selectedIntegrationType.name + ' Integration'
      }));
    }
  }, [integration, selectedIntegrationType]);

  useEffect(() => {
    if (formData.type_id) {
      const type = integrationTypes.find(t => t.type_id === formData.type_id);
      setSelectedType(type);
    } else {
      setSelectedType(null);
    }
  }, [formData.type_id, integrationTypes]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAdditionalConfigChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      additional_config: {
        ...prev.additional_config,
        [key]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Integration name is required');
      setLoading(false);
      return;
    }

    if (!formData.type_id) {
      setError('Integration type is required');
      setLoading(false);
      return;
    }

    if (!formData.api_key.trim() && !integration) {
      setError('API key is required');
      setLoading(false);
      return;
    }

    try {
      const submitData = { ...formData };
      
      // Don't include API key if it's empty (for updates)
      if (!submitData.api_key.trim() && integration) {
        delete submitData.api_key;
      }

      await onSubmit(submitData);
    } catch (err) {
      setError(err.message || 'Failed to save integration');
    } finally {
      setLoading(false);
    }
  };

  const renderConfigFields = () => {
    if (!selectedType || !selectedType.config_schema || !selectedType.config_schema.properties) {
      return null;
    }

    const properties = selectedType.config_schema.properties;
    return Object.entries(properties).map(([key, schema]) => {
      const value = formData.additional_config[key] || '';
      
      return (
        <div key={key} className="form-group">
          <label>
            {schema.title || key}
            {selectedType.config_schema.required?.includes(key) && ' *'}
          </label>
          {schema.description && (
            <small className="field-description">{schema.description}</small>
          )}
          {schema.type === 'boolean' ? (
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleAdditionalConfigChange(key, e.target.checked)}
              />
              <span>Enable {schema.title || key}</span>
            </label>
          ) : schema.enum ? (
            <select
              value={value}
              onChange={(e) => handleAdditionalConfigChange(key, e.target.value)}
              required={selectedType.config_schema.required?.includes(key)}
            >
              <option value="">Select {schema.title || key}...</option>
              {schema.enum.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <input
              type={schema.type === 'integer' ? 'number' : 'text'}
              value={value}
              onChange={(e) => handleAdditionalConfigChange(key, e.target.value)}
              placeholder={schema.description || `Enter ${schema.title || key}`}
              required={selectedType.config_schema.required?.includes(key)}
            />
          )}
        </div>
      );
    });
  };

  const renderAuthenticationFields = () => {
    const isHubSpot = selectedType?.type_id === 'hubspot' || 
                     (selectedIntegrationType && selectedIntegrationType.type_id === 'hubspot');
    const isStripe = selectedType?.type_id === 'stripe' || 
                    (selectedIntegrationType && selectedIntegrationType.type_id === 'stripe');

    const label = isHubSpot ? 'Access token' : isStripe ? 'Secret key' : 'API Key';

    return (
      <div className="form-group">
        <label>{label} {integration ? '' : '*'}</label>
        <input
          type="password"
          autoComplete="off"
          name="api_key"
          value={formData.api_key}
          onChange={handleChange}
          placeholder={integration ? "Leave blank to keep current key" : "Enter " + label}
          required={!integration}
        />
        <small className="field-description">
          {integration 
            ? "Leave blank to keep the current " + label
            : "The " + label + " will be securely encrypted"
          }
        </small>
      </div>
      /*<div className="form-group">
        <label>API Endpoint</label>
        <input
          type="url"
          name="api_endpoint"
          value={formData.api_endpoint}
          onChange={handleChange}
          placeholder="https://api.example.com (optional, uses default if empty)"
        />
        <small className="field-description">
          Leave blank to use the default endpoint for this integration type
        </small>
      </div>*/
    );
  };

  const renderHubSpotSpecificFields = () => {
    const isHubSpot = selectedType?.type_id === 'hubspot' || 
                     (selectedIntegrationType && selectedIntegrationType.type_id === 'hubspot');
    
    if (!isHubSpot) {
      return null;
    }

    const clientKeyValue = formData.additional_config.client_key || '';

    return (
      <div className="form-group">
        <label>Client secret {integration ? '' : '*'}</label>
        <input
          type="password"
          autoComplete="off"
          value={clientKeyValue}
          onChange={(e) => handleAdditionalConfigChange('client_key', e.target.value)}
          placeholder="Enter HubSpot 'Client secret' for webhooks"
          required={!integration}
        />
        <small className="field-description">
        Client secret for HubSpot webhook authentication.
        </small>
      </div>
    );
  };

  const renderCustomerioSpecificFields = () => {
    const isCustomerio = selectedType?.type_id === 'customerio' || 
                     (selectedIntegrationType && selectedIntegrationType.type_id === 'customerio');
    
    if (!isCustomerio) {
      return null;
    }

    const value = formData.additional_config.webhook_signing_secret || '';

    return (
      <div className="form-group">
        <label>Webhook signing key</label>
        <input
          type="password"
          autoComplete="off"
          value={value}
          onChange={(e) => handleAdditionalConfigChange('webhook_signing_secret', e.target.value)}
          placeholder="Leave black to keep current key"
          required={false}
        />
        <small className="field-description">
        Webhook signing key for Customer.io webhook authentication.
        </small>
      </div>
    );
  };

  const renderUserflowSpecificFields = () => {
    const isUserflow = selectedType?.type_id === 'userflow' || 
                      (selectedIntegrationType && selectedIntegrationType.type_id === 'userflow');
    
    if (!isUserflow) {
      return null;
    }

    const apiVersionValue = formData.additional_config.api_version || '2020-01-03';

    return (
      <>
        <div className="form-group">
          <label>API Version</label>
          <select
            value={apiVersionValue}
            onChange={(e) => handleAdditionalConfigChange('api_version', e.target.value)}
          >
            <option value="2020-01-03">2020-01-03 (Current)</option>
          </select>
          <small className="field-description">
            Userflow API version. Use 2020-01-03 (current stable version).
          </small>
        </div>
        <div className="info-message">
          <strong>🔄 Automatic Webhook Management</strong>
          <p>Webhook subscriptions are automatically created and managed for you. When this integration is enabled, a webhook subscription will be created in your Userflow account pointing to your TouchFlow instance.</p>
        </div>
      </>
    );
  };

  const renderUserpilotSpecificFields = () => {
    const isUserpilot = selectedType?.type_id === 'userpilot' || 
                       (selectedIntegrationType && selectedIntegrationType.type_id === 'userpilot');
    
    if (!isUserpilot) {
      return null;
    }

    const webhookSecretValue = formData.additional_config.webhook_secret || '';

    return (
      <>
        <div className="form-group">
          <label>Webhook Secret (Required for webhook security)</label>
          <input
            type="password"
            value={webhookSecretValue}
            onChange={(e) => handleAdditionalConfigChange('webhook_secret', e.target.value)}
            placeholder="Enter your Userpilot webhook secret token"
            required={!integration}
          />
          <small className="field-description">
            The secret token you'll configure in your Userpilot webhook. Required for webhook signature verification.
          </small>
        </div>
        <div className="info-message">
          <strong>📋 Manual Webhook Setup Required</strong>
          <p>Userpilot webhooks must be configured manually in your Userpilot dashboard. After creating this integration, use the webhook URL from the integration details and the secret token above to configure your webhook in Userpilot's Integrations → Webhooks section.</p>
        </div>
      </>
    );
  };

  const renderStripeSpecificFields = () => {
    const isStripe = selectedType?.type_id === 'stripe' || 
                    (selectedIntegrationType && selectedIntegrationType.type_id === 'stripe');
    
    if (!isStripe) {
      return null;
    }

    return (
      <div className="info-message">
        <strong>🔄 Automatic Webhook Management</strong>
        <p>Webhook endpoints are automatically created and managed via the Stripe API. When this integration is saved, a webhook endpoint will be registered in your Stripe account for subscription and invoice events. The signing secret is stored automatically.</p>
      </div>
    );
  };

  const renderLemlistSpecificFields = () => {
    const isLemlist = selectedType?.type_id === 'lemlist' ||
                     (selectedIntegrationType && selectedIntegrationType.type_id === 'lemlist');

    if (!isLemlist) {
      return null;
    }

    return (
      <div className="info-message">
        <strong>🔄 Automatic Webhook Management</strong>
        <p>Webhook subscriptions are automatically created and managed via the Lemlist API. When this integration is saved, a webhook will be registered in your Lemlist account to receive all campaign and lead activity events.</p>
      </div>
    );
  };

  const renderEsputnikSpecificFields = () => {
    const isEsputnik = selectedType?.type_id === 'esputnik' || 
                      (selectedIntegrationType && selectedIntegrationType.type_id === 'esputnik');
    
    if (!isEsputnik) {
      return null;
    }

    const webhookLoginValue = formData.additional_config.webhook_login || '';
    const webhookPasswordValue = formData.additional_config.webhook_password || '';

    return (
      <>
        <div className="form-group">
          <label>Webhook Login (Required for authentication)</label>
          <input
            type="text"
            value={webhookLoginValue}
            onChange={(e) => handleAdditionalConfigChange('webhook_login', e.target.value)}
            placeholder="Enter webhook authentication username"
            required={!integration}
          />
          <small className="field-description">
            Username for HTTP Basic Authentication that eSputnik will use when sending webhooks.
          </small>
        </div>
        <div className="form-group">
          <label>Webhook Password (Required for authentication)</label>
          <input
            type="password"
            value={webhookPasswordValue}
            onChange={(e) => handleAdditionalConfigChange('webhook_password', e.target.value)}
            placeholder="Enter webhook authentication password"
            required={!integration}
          />
          <small className="field-description">
            Password for HTTP Basic Authentication that eSputnik will use when sending webhooks.
          </small>
        </div>
      </>
    );
  };

  const getFormTitle = () => {
    if (integration) {
      return `Edit ${selectedType?.name || 'Integration'}`;
    } else if (selectedIntegrationType) {
      return `Add ${selectedIntegrationType.name} Integration`;
    } else {
      return 'Add New Integration';
    }
  };

  const DOCS_URLS = {
    hubspot: 'https://www.touchflow.cx/docs/hubspot',
    esputnik: 'https://www.touchflow.cx/docs/esputnik',
    stripe: 'https://www.touchflow.cx/docs/stripe',
    customerio: 'https://www.touchflow.cx/docs/customer.io',
  };

  const getDocsUrl = () => {
    const typeId = selectedType?.type_id || selectedIntegrationType?.type_id;
    return typeId ? DOCS_URLS[typeId] || null : null;
  };

  const getFormDescription = () => {
    if (selectedIntegrationType && !integration) {
      return selectedIntegrationType.description;
    } else if (selectedType) {
      return selectedType.description;
    }
    return null;
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <h3>{getFormTitle()}</h3>
            {getFormDescription() && (
              <p className="form-description">{getFormDescription()}</p>
            )}
            {getDocsUrl() && (
              <a href={getDocsUrl()} target="_blank" rel="noopener noreferrer" className="docs-link">
                📖 Setup guide
              </a>
            )}
          </div>
          <button className="close-modal" onClick={onCancel}>×</button>
        </div>
        
        <form className="integration-form" onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>Integration Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter a name for this integration"
              required
            />
          </div>

          <div className="form-group">
            <label>Integration Type *</label>
            {selectedIntegrationType && !integration ? (
              <div className="selected-type-display">
                <strong>{selectedIntegrationType.name}</strong>
                <small className="field-description">{selectedIntegrationType.description}</small>
              </div>
            ) : (
              <select
                name="type_id"
                value={formData.type_id}
                onChange={handleChange}
                required
                disabled={!!integration} // Don't allow changing type for existing integrations
              >
                <option value="">Select integration type...</option>
                {integrationTypes.map(type => (
                  <option key={type.type_id} value={type.type_id}>
                    {type.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Authentication Fields */}
          {renderAuthenticationFields()}

          {/* HubSpot-specific Fields */}
          {renderHubSpotSpecificFields()}

          {/* Userflow-specific Fields */}
          {renderUserflowSpecificFields()}

          {/* Userpilot-specific Fields */}
          {renderUserpilotSpecificFields()}

          {/* Stripe-specific Fields */}
          {renderStripeSpecificFields()}

          {/* Lemlist-specific Fields */}
          {renderLemlistSpecificFields()}

          {/* eSputnik-specific Fields */}
          {renderEsputnikSpecificFields()}

          {/* Customerio-specific Fields */}
          {renderCustomerioSpecificFields()}

          {/* Additional Configuration Fields */}
          {renderConfigFields()}

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="enabled"
                checked={formData.enabled}
                onChange={handleChange}
              />
              <span>Enable this integration</span>
            </label>
            <small className="field-description">
              Disabled integrations won't process any data
            </small>
          </div>

          <div className="modal-actions">
            <button type="button" className="secondary-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? 'Saving...' : (integration ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntegrationForm; 